import { auth0 } from './auth0';
import { FUNCTIONS_ORIGIN } from '@/utils/functionsOrigin';
import { trace } from '@/utils/authTrace';
// M5 item 1 (2026-11-01): a 401 that SURVIVES the forced refresh (or that no
// refreshed token could be minted for) is an expired session — the refresh token
// is gone or the tenant issues none — and nothing short of a new sign-in fixes
// it. It is marked here, once, so every screen can say the remedy that works
// instead of "reloading tries again" (src/auth/sessionExpired.js).
import { markSessionExpired } from './sessionExpired';

// Tenant-isolation: attach the caller's Auth0 access token to requests for the
// org-scoped onRequest Cloud Functions, so the backend (requireOrgMember) can
// verify identity + org membership. Implemented as a scoped fetch wrapper so we
// don't have to touch ~46 call sites individually and can't accidentally miss one.
//
// IMPORTANT — this is an ALLOWLIST, default-closed:
//  - Callable (onCall) functions (signupWithAuth0, createUser, disableAuth0Mfa,
//    updateStripeCustomer, fetchInvoices) go through the Firebase SDK /
//    httpsCallable to the SAME origin; they manage their own auth and must
//    NEVER receive this header, so they are deliberately excluded.
//  - stripeWebhook is an external webhook (Stripe-signed), also excluded.
// When you add a new org-scoped onRequest function + requireOrgMember, add its
// name here too, or the frontend won't send a token and it will 401.

const AUTHED_FUNCTIONS = new Set([
  'mintFirebaseToken',
  'updateUserEmail', 'updateUserName', 'submitForm', 'getFormData', 'saveFormTemplate',
  'deleteFormTemplate', 'updateFormSubmissionStatus', 'deleteFormSubmission', 'submitSupportRequest',
  'requestOrganisationDeletion',
  'backfillPersonNames', 'publishShifts', 'importShifts',
  // S1 (2026-10-30): the clock-out and the employee's own pay figures.
  'endShift', 'ownPayFigures', 'writeShift', 'writeShifts',
  // `migratePermissionViewNames` WAS HERE AND WAS REMOVED 2026-10-17. It is
  // gated by `verifyAuth0Token(..., adminAudiences(), ...)`
  // (`adminAudiences()` in functions/index.js), which rejects THIS SPA's ID token by audience —
  // so the entry attached a token that could never be accepted. Measured
  // before removing: the sole occurrence of the name in all of `src/` was this
  // line itself, so nothing in the frontend called it and the entry had no
  // effect to lose. A leftover from the Phase 1.0 admin audience separation.
  // T1 (brief-comms-chat, 2026-10-27) — the conversation handlers. Each is
  // org-scoped and calls requireOrgMember or requireOrgCapability, so each
  // needs the token exactly like the post handlers beside them. The three
  // mirror TRIGGERS are deliberately absent: a trigger is not fetched by any
  // client, and listing one would be meaningless.
  'commsMessage', 'commsReply', 'deleteCommsMessage', 'commsMessageAttachment',
  'createConversation', 'updateConversationMembers',
  // T7 (2026-10-28) — `addPost`, `deleteCommsPost`, `commsAttachments` and
  // `commsComment` WERE HERE AND WERE REMOVED. The posts feed was deleted and
  // `/comms` is the chat, so nothing in this SPA fetches any of the four. The
  // HANDLERS STAY DEPLOYED, deliberately: the migration reads posts, and a
  // later posts-deletion script removes the functions and the rules together.
  // Until then an entry here would attach a token to a call nobody makes.
  //
  // ⚠ WHICH MEANS THE DERIVED TEST HAS A CARVE-OUT, and it is named rather than
  // quiet: `FEED_ENDPOINTS_WITHOUT_A_CLIENT` in
  // src/tests/clientAllowlistCoversAuthedEndpoints.test.js lists these four and
  // asserts each is still gated in functions/index.js — so when the deletion
  // script removes the handlers, that assertion goes red and the carve-out has
  // to go with them. It drains itself; it is not an exceptions map.
  'addPerson', 'dayNotes', 'addLocation', 'deleteLocation', 'enableAuth0Mfa', 'activateAndInviteUser', 'savePermissions', 'deletePermissionGroup',
  'updateNewUserFlag', 'deleteUser', 'deleteShift', 'deleteDraftShift', 'updatePerson', 'retirePerson',
  'unarchivePerson', 'addLeaveRequest', 'removeLeaveRequest', 'updateLeaveStatus', 'addUnavailability',
  'removeUnavailability', 'updatePersonWages', 'bulkApplyWageProfile', 'saveWageProfile',
  'deleteWageProfile', 'clearPersonError', 'revalidateOrgAwardMinimums',
  'createCheckoutSession', 'finaliseCheckoutSession', 'createBillingPortalSession', 'updateUserPassword', 'unsubscribe',
  'uploadOrgLogo', 'uploadFormFile', 'updateFeatureFlags', 'updateOrgToggles',
  // Xero phase 2, T2 (2026-10-13). `xeroConnect` is org-scoped and calls
  // requireOrgCapability, so it needs the token like every entry above.
  //
  // `xeroCallback` IS DELIBERATELY ABSENT, for the same reason stripeWebhook is:
  // it is an inbound redirect from Xero, a top-level browser navigation that no
  // client code ever fetches. Listing it would be meaningless at best, and the
  // list is the one place somebody would add it "for consistency".
  'xeroConnect', 'xeroDisconnect',
  // Integrations page, T3 (2026-10-13). Org-scoped and calls requireOrgMember,
  // so it needs the token like every entry above. The whole point of the
  // endpoint is that the organisation and the person are SERVER-derived rather
  // than taken from the body — without the token it cannot derive either.
  'requestIntegration',
  // Xero phase 3, T1 (2026-10-14). Org-scoped, calls requireOrgCapability with
  // the same `Export Timesheets` view as connect and disconnect.
  'xeroDiscover',
  // Xero phase 3, T3 (2026-10-15). Writes one entry of the rule map. Same gate
  // again: mapping hours onto an earnings rate decides what reaches a payslip,
  // which is the same capability as connecting the payroll system it reaches.
  // ⚠ WITHOUT THIS ENTRY THE CALL IS UNAUTHENTICATED AND THE GATE 401s — the
  // defect memory/firestore-write-false-cloud-function-pattern.md records, and
  // it fails in a way that looks like a permissions bug rather than a missing
  // allowlist line.
  'xeroSetRuleMapping',
  // Xero phase 3, T4 (2026-10-15). Reads the venue's Xero employees and writes
  // the people it could link. Same gate; and the same ⚠ as above — without this
  // line the call is unauthenticated and the gate 401s, which reads as a
  // backend permissions bug rather than a missing allowlist entry.
  'xeroAutoLinkPeople',
  // Xero phase 4, T2 (2026-10-15). THE FIRST ENDPOINT IN ROSTEREM THAT WRITES
  // TO XERO. Same `Export Timesheets` gate; and the same ⚠ as above — without
  // this line the call is unauthenticated and the gate 401s.
  //
  // ⚠ ITS `dryRun` FIELD IS REQUIRED BY THE SERVER, not defaulted. Any caller
  // added later — the export screen, a per-row resend, a console fetch during a
  // live check — must send it explicitly or receive a 400. A client that omits
  // it cannot accidentally post somebody's hours.
  'xeroExportTimesheet',
  // Xero phase 4, T3 (2026-10-17). THE BATCH. Same `Export Timesheets` gate as
  // T2 — the `requireOrgCapability(request, organisationId, 'Export Timesheets')` call in
  // `functions/xeroExportBatch.js :: handleXeroExportBatch` is byte-identical to the one in
  // `functions/xeroExportOne.js :: handleXeroExportOne`.
  // Both re-derived 2026-10-23 (T4's inserts shifted the single-export one, and
  // the batch number had drifted earlier); ANCHORED this time, so the next
  // shift fails by name instead of silently pointing at a blank line.
  //
  // ⚠ THIS LINE IS THE WHOLE REASON THE FIRST LIVE DRY RUN RETURNED 401.
  // The endpoint was built, wired, tested and deployed; the name was not added
  // here, so `:122` passed the request through with no token and the gate
  // refused a caller it never saw. Three calls produced NO `[auth-trace]`
  // lines at all, which is the unmatched branch by construction. The warning
  // above `xeroSetRuleMapping` had already been written twice, by the same
  // author, and the next endpoint fell into it anyway — which is why
  // `src/tests/clientAllowlistCoversAuthedEndpoints.test.js` now derives this
  // list from `functions/index.js` instead of asking anyone to remember.
  'xeroExportBatch',
]);

function functionName(url) {
  if (typeof url !== 'string' || !url.startsWith(FUNCTIONS_ORIGIN)) return null;
  const rest = url.slice(FUNCTIONS_ORIGIN.length + 1); // strip leading "/"
  const name = rest.split(/[/?#]/)[0];
  return name || null;
}

// #71 (2026-08-03): forces the SDK to check/refresh the ID token via
// idTokenClaims (see auth0.js's own comment on why getIdTokenClaims() isn't
// used). getAccessTokenSilently()'s return value is discarded — we don't
// request an audience, so there's no custom access token to send — but
// calling it is what makes the SDK actually refresh, and it updates
// idTokenClaims.value as a side effect (Auth0Plugin.__refreshState(), run
// unconditionally in a `finally` after every call, cache hit or miss). With
// cacheMode 'on' (default) this returns fast from cache unless the cached
// token is genuinely near/past expiry; 'off' forces a real refresh-token
// exchange, bypassing the cache entirely.
async function ensureFreshToken(forceRefresh) {
  trace('getAccessTokenSilently-start', { caller: 'functionAuthInterceptor', forceRefresh: !!forceRefresh });
  const startedAt = performance.now();
  try {
    await auth0.getAccessTokenSilently(forceRefresh ? { cacheMode: 'off' } : undefined);
    trace('getAccessTokenSilently-resolved', {
      caller: 'functionAuthInterceptor',
      forceRefresh: !!forceRefresh,
      durationMs: Math.round(performance.now() - startedAt),
    });
  } catch (e) {
    // Not authenticated yet (pre-login), or the refresh token itself is
    // gone/revoked/expired — idTokenClaims stays whatever it already was.
    // Falls through exactly as before this fix: request goes out with a
    // stale/absent token, backend 401s.
    trace('getAccessTokenSilently-rejected', {
      caller: 'functionAuthInterceptor',
      forceRefresh: !!forceRefresh,
      durationMs: Math.round(performance.now() - startedAt),
      message: e && e.message,
      error: e && e.error,
    });
  }
  return auth0.idTokenClaims?.value?.__raw;
}

export function installFunctionAuthInterceptor() {
  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    init = init || {};
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const name = functionName(url);
    if (!name || !AUTHED_FUNCTIONS.has(name)) {
      return nativeFetch(input, init);
    }

    const baseHeaders = () =>
      new Headers(init.headers || (typeof input !== 'string' ? input.headers : undefined));

    // Send the Auth0 ID token (a signed OIDC JWT) — NOT an access token. The
    // backend's verifyAuth0Token validates this ID token by issuer + audience
    // (this SPA's client id); moving to a proper access token + API audience
    // is a recorded future hardening (#71), not this fix.
    const token = await ensureFreshToken(false);
    const firstHeaders = baseHeaders();
    if (!firstHeaders.has('Authorization') && token) {
      firstHeaders.set('Authorization', `Bearer ${token}`);
    }
    const response = await nativeFetch(input, { ...init, headers: firstHeaders });

    if (response.status !== 401) return response;

    // #71: a long-backgrounded tab's cached ID token can be genuinely expired
    // by the time a request actually fires (the proactive check above only
    // catches expiry the SDK's own clock-skew-aware cache already flagged).
    // One forced refresh + one retry — never more than that, so a real auth
    // failure (revoked session, wrong org) still surfaces as a 401 to the
    // caller, same as before this fix. Always overrides Authorization on
    // retry, even for the handful of call sites that set their own — a 401 on
    // attempt 1 means whatever token was used, caller-supplied or not, is
    // exactly what needs replacing.
    const refreshedToken = await ensureFreshToken(true);
    if (!refreshedToken) { markSessionExpired(); return response; }
    const retryHeaders = baseHeaders();
    retryHeaders.set('Authorization', `Bearer ${refreshedToken}`);
    const retried = await nativeFetch(input, { ...init, headers: retryHeaders });
    if (retried.status === 401) markSessionExpired();
    return retried;
  };
}
